/**
 * pages/StudentQueuePage.jsx
 * Session is instructor-scoped. Student finds active sessions
 * from their instructors, picks if multiple, joins one queue per instructor.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useQueueSSE } from "../hooks/useQueueSSE";
import AnnouncementBanner from "../components/shared/AnnouncementBanner";
import NedryModal from "../components/shared/NedryModal";
import ProfileCard from "../components/shared/ProfileCard";
import { useChime } from "../hooks/useChime";

import styles from "./StudentQueuePage.module.css";

/**
 * The estimated wait, held steady between refreshes.
 *
 * /status is polled every 5s, but a number that visibly ticks and occasionally
 * jumps backwards on a reorder reads as a broken countdown rather than an
 * estimate. So the newest value is only swapped in once the instructor's
 * configured refresh interval has elapsed — the first one shows immediately,
 * and after that it changes on a slow, deliberate cadence.
 *
 * Renders nothing when there's no prediction: the feature being off, suppressed
 * during pooling, or hidden from students all arrive here as null, and silence
 * is a valid answer rather than a failure to report.
 */
function WaitPredictionLine({ prediction }) {
  const [shown, setShown] = useState(null);
  const shownAtRef = useRef(0);

  useEffect(() => {
    if (!prediction) {
      setShown(null);
      shownAtRef.current = 0;
      return;
    }
    const holdMs = Math.max(60, prediction.refresh_seconds || 180) * 1000;
    const now = Date.now();
    if (shownAtRef.current === 0 || now - shownAtRef.current >= holdMs) {
      setShown(prediction);
      shownAtRef.current = now;
    }
  }, [prediction]);

  if (!shown) return null;

  return (
    <div
      style={{
        marginTop: 10,
        padding: "8px 14px",
        borderRadius: "var(--radius-md)",
        background: "var(--bg-card)",
        border: "1px solid var(--border)",
        fontSize: 13,
        color: "var(--txt)",
      }}
    >
      {shown.text}
      <div style={{ fontSize: 11, color: "var(--txt-faint)", marginTop: 3 }}>
        An estimate — it moves as the queue does.
      </div>
    </div>
  );
}

const POLICY_LABELS = {
  FCFS: "First-Come, First-Served",
  POINTS: "Points-Based (longest wait gets priority)",
  RANDOMIZED: "Randomized Order",
  POOLED_FCFS: "Pooled — randomized once at start, then first-come",
};

/**
 * Live countdown to the pooled-shuffle deadline, shown in the student pooling
 * banner. Ticks every second; at zero it shows that the order is being locked
 * in (the server fires the shuffle on its next read/scheduler pass). Renders
 * nothing if there's no deadline — e.g. a manually opened pooled session that
 * relies on the instructor's "shuffle now" button instead of a timed deadline.
 */
function PoolCountdownLine({ deadline }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!deadline) return null;
  const msLeft = new Date(deadline).getTime() - now;
  if (msLeft <= 0) {
    return (
      <div style={{ marginTop: 6, fontWeight: 600, color: "var(--amber)" }}>
        Locking in the order now…
      </div>
    );
  }
  const total = Math.floor(msLeft / 1000);
  const mm = String(Math.floor(total / 60)).padStart(2, "0");
  const ss = String(total % 60).padStart(2, "0");
  return (
    <div style={{ marginTop: 6 }}>
      Queue starts in{" "}
      <span
        style={{
          fontFamily: "var(--font-mono, monospace)",
          fontWeight: 700,
          color: "var(--amber)",
        }}
      >
        {mm}:{ss}
      </span>
    </div>
  );
}

/**
 * What a student sees before joining: how busy this queue is, and how it's
 * ordered. Shown whether one session is open or several — the picker only
 * appears when there's a choice to make, but "should I bother joining right
 * now" is a question either way.
 *
 * Renders nothing without load data, so an older backend, a failed enrichment
 * or a restored localStorage session degrades to the plain join screen rather
 * than an empty box.
 */
function QueueStatusLine({ session }) {
  if (!session) return null;

  const hasCount = typeof session.waiting === "number";
  const policy = POLICY_LABELS[session.policy] || session.policy;
  const pooling = session.policy === "POOLED_FCFS";

  if (!hasCount && !session.load_label && !policy) return null;

  return (
    <div
      className="card"
      style={{
        padding: "12px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      {hasCount && (
        <div style={{ fontSize: 14, color: "var(--txt)" }}>
          <strong>{session.waiting}</strong>{" "}
          {session.waiting === 1 ? "student" : "students"}{" "}
          {pooling ? "pooling" : "waiting"}
        </div>
      )}

      {/* Deliberately a band, not a figure. A count says nothing about how long
          each visit takes, and a number that ticks between renders makes
          everyone pile into whichever queue is momentarily shorter.
          Suppressed while pooling, where there is no order yet to estimate
          against. */}
      {!pooling && session.load_label && (
        <div style={{ fontSize: 13, color: "var(--txt-dim)" }}>
          {session.load_label}
          {session.load_source === "count" && " (estimated from queue length)"}
        </div>
      )}

      {policy && (
        <div style={{ fontSize: 12, color: "var(--txt-faint)" }}>
          {policy}
        </div>
      )}
    </div>
  );
}

function CompetencyPicker({ session, loading, onJoin }) {
  const { apiFetch } = useAuth();
  const [competencies, setCompetencies] = useState([]);
  const [selected, setSelected] = useState(undefined);
  const [loadingComps, setLoadingComps] = useState(false);

  // Fetch competencies for the student's course with this instructor
  useEffect(() => {
    if (!session?.session_id) return;
    setLoadingComps(true);
    setSelected(undefined);
    // Get competencies for the student's courses with this instructor
    apiFetch(`/api/competencies/session/${session.session_id}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        setCompetencies(data);
        setLoadingComps(false);
      })
      .catch(() => setLoadingComps(false));
  }, [session?.session_id]); // eslint-disable-line

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {loadingComps ? (
        <p
          style={{ color: "var(--txt-dim)", fontSize: 13, textAlign: "center" }}
        >
          Loading…
        </p>
      ) : competencies.length === 0 ? (
        <button
          className="btn-primary"
          style={{
            width: "100%",
            padding: 16,
            fontSize: 16,
            fontWeight: 700,
            borderRadius: "var(--radius-md)",
          }}
          onClick={() => onJoin(null, null)}
          disabled={loading}
        >
          {loading ? "Joining…" : "Join Queue"}
        </button>
      ) : (
        <>
          <p
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "var(--txt-dim)",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            What are you here for?
          </p>
          {competencies.map((c) => (
            <button
              key={`${c.course_competency_id ?? "questions"}-${c.course_id}`}
              onClick={() =>
                setSelected({
                  competency_id: c.course_competency_id,
                  course_id: c.course_id,
                })
              }
              style={{
                width: "100%",
                padding: "12px 16px",
                textAlign: "left",
                background:
                  selected?.competency_id === c.course_competency_id &&
                  selected?.course_id === c.course_id
                    ? "var(--gold-bg)"
                    : "var(--bg-card)",
                border: `1px solid ${
                  selected?.competency_id === c.course_competency_id &&
                  selected?.course_id === c.course_id
                    ? "var(--gold)"
                    : "var(--border)"
                }`,
                borderRadius: "var(--radius-md)",
                color:
                  selected?.competency_id === c.course_competency_id &&
                  selected?.course_id === c.course_id
                    ? "var(--gold)"
                    : "var(--txt)",
                fontSize: 14,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              <span style={{ fontWeight: 600 }}>{c.name}</span>
              {c.course_code && (
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--txt-faint)",
                    marginLeft: 8,
                  }}
                >
                  {c.course_code}
                </span>
              )}
              {c.course_competency_id === null && (
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--txt-faint)",
                    marginLeft: 8,
                  }}
                >
                  unlimited
                </span>
              )}
            </button>
          ))}
          <button
            className="btn-primary"
            style={{
              width: "100%",
              padding: 14,
              fontSize: 15,
              fontWeight: 700,
              borderRadius: "var(--radius-md)",
              marginTop: 4,
            }}
            onClick={() => onJoin(selected?.competency_id, selected?.course_id)}
            disabled={loading || selected === undefined}
          >
            {loading ? "Joining…" : "Join Queue"}
          </button>
        </>
      )}
    </div>
  );
}

function SessionPicker({ sessions, onPick }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <p style={{ fontSize: 14, color: "var(--txt-dim)", textAlign: "center" }}>
        Multiple instructors have open queues. Which would you like to join?
      </p>
      {sessions.map((s) => (
        <button
          key={s.session_id}
          className="btn-secondary"
          style={{
            width: "100%",
            padding: "14px 16px",
            fontSize: 15,
            fontWeight: 600,
            borderRadius: "var(--radius-md)",
          }}
          onClick={() => onPick(s)}
        >
          {s.instructor_name}'s Queue
        </button>
      ))}
    </div>
  );
}

export default function StudentQueuePage() {
  const { user, token, apiFetch, logout } = useAuth();

  const [availableSessions, setAvailableSessions] = useState([]);
  const [session, setSession] = useState(null);
  const [queueStatus, setQueueStatus] = useState(null);
  const [showProfile, setShowProfile] = useState(false);

  // Off by default — a sound nobody asked for is worse than no sound. The
  // screen already changes when you're called; this is for anyone who has
  // looked away.
  const chime = useChime("gradiance_student_chime", { defaultOn: false });
  const [entries, setEntries] = useState([]);
  const [announcement, setAnnouncement] = useState("");
  // On-deck text. Server sends the resolved value (session override ->
  // instructor default -> built-in); this initial value only shows if the
  // status payload hasn't arrived yet.
  const [nextUpMessage, setNextUpMessage] = useState(
    "You're next — get to the chair outside of the office.",
  );
  const [directMessage, setDirectMessage] = useState("");
  const [removalNotice, setRemovalNotice] = useState("");
  const [sessionEnded, setSessionEnded] = useState(false);
  const sessionEndedRef = useRef(false);
  const [isPaused, setIsPaused] = useState(false);
  const [pauseMessage, setPauseMessage] = useState("");
  const [isLocked, setIsLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(true);
  const [error, setError] = useState("");
  const [nedry, setNedry] = useState(null);
  const [swapRequests, setSwapRequests] = useState([]);
  const [multipleWarning, setMultipleWarning] = useState(false);

  const pauseMessageRef = useRef("");

  // The position last seen in an SSE payload, and a sequence number for
  // /status requests.
  //
  // Both exist because of the same race. _broadcast_queue runs inside the
  // request transaction, before it commits: the pushed payload already has the
  // renumbered positions, but the GET /status it triggers opens a *new*
  // transaction that may still read the pre-commit values. The count comes
  // from the payload and is right; the position comes from that fetch and can
  // be stale — which is exactly what students reported.
  //
  // The payload is therefore treated as authoritative for position, and
  // out-of-order /status responses are discarded.
  const ssePositionRef = useRef(null);
  const statusSeqRef = useRef(0);

  useEffect(() => {
    findActiveSessions();
  }, []); // eslint-disable-line

  // While no session is active, quietly re-check so a calendar auto-opened
  // queue appears on its own — no page refresh needed.
  useEffect(() => {
    if (session) return;
    const id = setInterval(() => findActiveSessions(true), 45000);
    return () => clearInterval(id);
  }, [session]); // eslint-disable-line

  const findActiveSessions = async (silent = false) => {
    if (!silent) setSearching(true);
    try {
      // The active-sessions list is the source of truth for what's open right
      // now. Fetch it first, then decide whether any saved session is still
      // valid — never trust a stale localStorage session on its own.
      const res = await apiFetch("/api/queue/active-sessions");
      if (!res.ok) {
        if (!silent) setSearching(false);
        return;
      }
      const sessions = await res.json();

      const saved = localStorage.getItem("gradiance_student_session");
      const savedObj = saved ? JSON.parse(saved) : null;
      const live = savedObj
        ? sessions.find((s) => s.session_id === savedObj.session_id)
        : null;

      if (live) {
        // Saved session is still active — restore it (refreshed from live data)
        const sessionObj = {
          session_id: live.session_id,
          instructor_id: live.instructor_id,
          instructor_name: live.instructor_name,
          policy: live.policy,
          // Taken from `live`, not from the saved copy: this runs on every
          // silent re-check, so the pre-join status stays current instead of
          // showing whatever the queue looked like when the tab was opened.
          waiting: live.waiting,
          load_label: live.load_label,
          load_source: live.load_source,
          estimated_minutes: live.estimated_minutes,
        };
        setSession(sessionObj);
        setAvailableSessions([]);
        localStorage.setItem(
          "gradiance_student_session",
          JSON.stringify(sessionObj),
        );
        await loadMyStatus(sessionObj.session_id);
        return;
      }

      // Saved session is gone or closed — drop it and rediscover.
      if (savedObj) localStorage.removeItem("gradiance_student_session");

      if (sessions.length === 0) {
        setSession(null);
        setAvailableSessions([]);
      } else if (sessions.length === 1) {
        // Only one instructor has an open queue — go straight in
        await pickSession(sessions[0]);
      } else {
        // Multiple instructors — show picker
        setAvailableSessions(sessions);
        setSession(null);
      }
    } catch {
      /* ignore */
    } finally {
      if (!silent) setSearching(false);
    }
  };

  const pickSession = async (sessionInfo) => {
    const sessionObj = {
      session_id: sessionInfo.session_id,
      instructor_id: sessionInfo.instructor_id,
      instructor_name: sessionInfo.instructor_name,
      policy: sessionInfo.policy,
      // Carried through so the pre-join status line can show how busy the
      // queue is. Previously dropped here, which is why the load fields from
      // /active-sessions never reached the render.
      waiting: sessionInfo.waiting,
      load_label: sessionInfo.load_label,
      load_source: sessionInfo.load_source,
      estimated_minutes: sessionInfo.estimated_minutes,
    };
    setSession(sessionObj);
    setAvailableSessions([]);
    localStorage.setItem(
      "gradiance_student_session",
      JSON.stringify(sessionObj),
    );
    await loadMyStatus(sessionInfo.session_id);
  };

  const loadMyStatus = async (sessionId) => {
    // Several of these can be in flight at once when events arrive quickly.
    // Without this, an older response can land last and overwrite a newer one,
    // which is how a stale position used to persist instead of self-correcting.
    const seq = ++statusSeqRef.current;
    try {
      const res = await apiFetch(`/api/queue/${sessionId}/status`);
      if (seq !== statusSeqRef.current) return;   // superseded
      if (res.ok) {
        const data = await res.json();
        if (seq !== statusSeqRef.current) return; // superseded while parsing
        if (data.session_active === false) {
          // The session was closed. Show the same sticky "ended" notice as the
          // SSE path rather than blanking the page out from under the student.
          // Guarded so this poll-driven fallback doesn't clobber a more specific
          // message the session_closed SSE event may have already set, and
          // doesn't re-trigger every 5s once we're already in the ended state.
          localStorage.removeItem("gradiance_student_session");
          if (!sessionEndedRef.current) {
            sessionEndedRef.current = true;
            setSessionEnded(true);
            setRemovalNotice(
              (prev) =>
                prev || "The queue session has been closed by the instructor.",
            );
            setQueueStatus((prev) =>
              prev
                ? { ...prev, in_queue: false, position: null, status: null }
                : prev,
            );
          }
          return;
        }
        // The SSE payload is built from the same transaction that renumbered
        // the queue, so where the two disagree it is the one to trust.
        const fresh =
          data.in_queue && ssePositionRef.current != null
            ? { ...data, position: ssePositionRef.current }
            : data;

        setQueueStatus(fresh);
        setAnnouncement(data.announcement || "");
        if (data.msg_next_up) setNextUpMessage(data.msg_next_up);
        setIsPaused(data.session_paused);
        setIsLocked(data.swap_locked);
      }
    } catch {
      /* ignore */
    }
  };

  useQueueSSE(session?.session_id, token, {
    onQueueUpdated: useCallback(
      (data) => {
        void data.timestamp;
        const list = data.entries || [];
        setEntries(list);

        // Our own position is already in this payload, so take it directly
        // rather than waiting on a round trip that can read pre-commit state.
        // Falls back silently to the old behaviour if the entry isn't found.
        const mine = user?.id
          ? list.find((e) => e.student_id === user.id)
          : undefined;
        ssePositionRef.current = mine ? mine.position : null;
        if (mine) {
          setQueueStatus((prev) =>
            prev && prev.in_queue ? { ...prev, position: mine.position } : prev,
          );
        }

        setIsPaused(data.is_paused);
        setIsLocked(data.is_locked || false);
        setAnnouncement(data.announcement || "");
        if (!data.is_paused) {
          pauseMessageRef.current = "";
          setPauseMessage("");
        }
        if (session?.session_id) loadMyStatus(session.session_id);
      },
      [session?.session_id, user?.id],
    ), // eslint-disable-line

    onStudentCalled: useCallback(
      (data) => {
        if (data.student_id === user?.id) {
          setQueueStatus((prev) =>
            prev ? { ...prev, status: "CALLED", in_queue: true } : prev,
          );
          // Only for this student's own call — not every call in the session.
          chime.play();
        }
      },
      [user?.id, chime],
    ),

    onPaused: useCallback((data) => {
      setIsPaused(data.is_paused);
      if (data.is_paused && data.message) {
        pauseMessageRef.current = data.message;
        setPauseMessage(data.message);
      } else {
        pauseMessageRef.current = "";
        setPauseMessage("");
      }
    }, []),

    onSessionClosed: useCallback((data) => {
      // Show the reason as a sticky notice that stays until the student
      // dismisses it, rather than blanking the page instantly (which is why the
      // old message flashed and vanished). Clearing our in-queue status drops
      // the queue view to a calm "ended" state behind the notice; dismissing the
      // notice (its ✕) then does the real teardown via the effect below.
      setRemovalNotice(
        (data && data.message) ||
          "The queue session has been closed by the instructor.",
      );
      sessionEndedRef.current = true;
      setSessionEnded(true);
      setQueueStatus((prev) =>
        prev
          ? { ...prev, in_queue: false, position: null, status: null }
          : prev,
      );
      localStorage.removeItem("gradiance_student_session");
    }, []), // eslint-disable-line

    onAnnouncement: useCallback((data) => setAnnouncement(data.message), []),
    onDirectMessage: useCallback(
      (data) => {
        if (data.student_id !== user?.id) return;
        if (data.removed) {
          // Instructor removed us (single remove or clear queue): show a sticky
          // notice and drop our stale in-queue status so the count doesn't linger.
          setRemovalNotice(data.message);
          setDirectMessage("");
          setQueueStatus((prev) =>
            prev
              ? { ...prev, in_queue: false, position: null, status: null }
              : prev,
          );
        } else {
          setDirectMessage(data.message);
        }
      },
      [user?.id],
    ),

    onSwapRequested: useCallback(
      (data) => {
        if (data.target_id === user?.id)
          setSwapRequests((prev) => [...prev, data]);
      },
      [user?.id],
    ),

    _onPoll: useCallback(() => {
      if (session?.session_id) loadMyStatus(session.session_id);
    }, [session?.session_id]), // eslint-disable-line
  });

  const joinQueue = async (courseCompetencyId = null, courseId = null) => {
    if (!session) return;

    // Warn if already in another queue
    if (multipleWarning === false && availableSessions.length > 0) {
      // Check if already in a queue for another instructor
      // (handled by warning state set when joining second queue)
    }

    setLoading(true);
    setError("");
    try {
      const body = { course_competency_id: courseCompetencyId };
      if (courseId) body.course_id = courseId;

      const res = await apiFetch(`/api/queue/${session.session_id}/join`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      let data = null;
      try {
        data = await res.json();
      } catch {
        /* non-JSON body (e.g. a server error page) */
      }
      if (res.ok) {
        setRemovalNotice("");
        await loadMyStatus(session.session_id);
      } else if (data?.detail?.violation) {
        setNedry({
          message: data.detail.message,
          code: data.detail.code,
          imageUrl: data.detail.image_url || null,
          imageAlt: data.detail.image_alt || "",
        });
      } else {
        setError(
          (data && data.detail) ||
            `Could not join queue (error ${res.status}).`,
        );
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const leaveQueue = async () => {
    if (!session) return;
    setLoading(true);
    try {
      await apiFetch(`/api/queue/${session.session_id}/leave`, {
        method: "DELETE",
      });
      setQueueStatus((prev) =>
        prev
          ? { ...prev, in_queue: false, position: null, status: null }
          : prev,
      );
      localStorage.removeItem("gradiance_student_session");
      setSession(null);
      // Re-check for other active sessions
      setTimeout(findActiveSessions, 500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const respondToSwap = async (swapId, accept) => {
    try {
      await apiFetch(`/api/queue/${session.session_id}/swap/${swapId}`, {
        method: "PUT",
        body: JSON.stringify({ accept }),
      });
      setSwapRequests((prev) => prev.filter((s) => s.swap_id !== swapId));
      if (accept) await loadMyStatus(session.session_id);
    } catch (err) {
      setError(err.message);
    }
  };

  const isCalled = queueStatus?.status === "CALLED";
  const isInQueue = queueStatus?.in_queue;
  const position = queueStatus?.position;
  const total = queueStatus?.total_waiting || 0;
  const rules = queueStatus?.rules;
  const pooling = queueStatus?.pooling;
  // Trust the server-computed flag rather than re-deriving from the policy
  // string (which can serialize as the enum name or its value).
  const isPooling = !!pooling?.is_pooling;

  // WCAG 4.1.3 (Status Messages). Position changes arrive over SSE and polling
  // and are conveyed purely by a number changing on screen — nothing a screen
  // reader would ever announce. These two regions carry that state to anyone
  // not looking at it. Split by urgency on purpose: position updates are
  // polite, so they wait for a pause rather than interrupting; being called is
  // assertive, because it's the one message that can't wait.
  const politeStatus = (() => {
    if (!isInQueue || isCalled) return "";
    if (isPooling)
      return "You're in the queue. Positions are still being drawn.";
    if (isPaused)
      return `The queue is paused. You are number ${position} of ${total}.`;
    if (position === 1) return "You are next in line.";
    if (position) return `You are number ${position} of ${total} in the queue.`;
    return "";
  })();

  const urgentStatus = isCalled
    ? "It's your turn now. Your instructor is ready for you."
    : "";

  return (
    <div className={styles.page}>
      {/* First tab stop on the page: jump past the header to the queue itself. */}
      <a href="#main-content" className="skip-link">
        Skip to queue
      </a>

      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {politeStatus}
      </div>
      <div className="sr-only" role="alert" aria-atomic="true">
        {urgentStatus}
      </div>

      <header className={styles.header}>
        <h1 className={styles.logo}>
          Gradiance
          <span className={styles.dot} aria-hidden="true">
            .
          </span>
        </h1>
        <div className={styles.userInfo}>
          <span className={styles.userName}>{user?.display_name}</span>
          <button
            className="btn-ghost"
            onClick={chime.toggle}
            aria-pressed={chime.enabled}
            title={
              chime.enabled
                ? "Sound on — you'll hear a chime when it's your turn. Click to turn off."
                : "Sound off — click to be alerted when it's your turn."
            }
          >
            <span aria-hidden="true">{chime.enabled ? "🔔" : "🔕"}</span>
            <span className="sr-only">
              {chime.enabled
                ? "Turn off the sound alert for your turn"
                : "Turn on a sound alert for your turn"}
            </span>
          </button>
          <button
            className="btn-ghost"
            onClick={() => setShowProfile((v) => !v)}
            title="Set your display name and pronouns"
          >
            Profile
          </button>
          {/* Plain <a>, not <Link>: the manual is static HTML in public/, not a
              route, so <Link> would be handled client-side and fall through to
              the SPA catch-all. Hidden on phones (see .headerManual) where the
              header row has no space — the page footer link covers that case. */}
          <a
            className={`btn-ghost ${styles.headerManual}`}
            href="/manual/"
            title="How the queue works, what to expect in an interview"
          >
            Manual
          </a>
          <button className="btn-ghost" onClick={logout}>
            Sign out
          </button>
        </div>
      </header>

      {showProfile && (
        <div
          style={{
            maxWidth: 480,
            width: "100%",
            margin: "16px auto 0",
            padding: "0 20px",
          }}
        >
          <ProfileCard compact onSaved={() => setShowProfile(false)} />
        </div>
      )}

      {removalNotice && (
        <div
          style={{
            background: "rgba(248,113,113,0.12)",
            border: "1px solid rgba(248,113,113,0.4)",
            borderRadius: 8,
            padding: "14px 16px",
            margin: "8px 16px",
            fontSize: 14,
            color: "var(--red, #e5484d)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <span>🚪 {removalNotice}</span>
          <button
            className="btn-ghost"
            onClick={() => {
              setRemovalNotice("");
              // If this notice was a session close (not just a queue clear), the
              // session is really gone — tear down now that the student has read
              // the reason, and look for any other active session.
              if (sessionEnded) {
                setSessionEnded(false);
                sessionEndedRef.current = false;
                setSession(null);
                setQueueStatus(null);
                setTimeout(findActiveSessions, 300);
              }
            }}
            style={{ fontSize: 12, padding: "2px 6px", flexShrink: 0 }}
          >
            ✕
          </button>
        </div>
      )}

      {isPooling && isInQueue && (
        <div
          style={{
            background: "rgba(251,191,36,0.1)",
            border: "1px solid rgba(251,191,36,0.35)",
            borderRadius: 8,
            padding: "12px 16px",
            margin: "8px 16px",
            fontSize: 13,
            color: "var(--txt)",
            lineHeight: 1.5,
          }}
        >
          <div
            style={{ fontWeight: 600, color: "var(--amber)", marginBottom: 4 }}
          >
            🎲 Pooling in progress
          </div>
          Everyone who joins before the queue starts is placed in a{" "}
          <strong>random</strong> order — joining early doesn't get you a better
          spot right now. When pooling ends, positions lock in and it becomes
          first-come, first-served for anyone who joins after.
          <PoolCountdownLine deadline={pooling?.pool_deadline} />
        </div>
      )}

      {directMessage && (
        <div
          style={{
            background: "rgba(96,165,250,0.1)",
            border: "1px solid rgba(96,165,250,0.3)",
            borderRadius: 8,
            padding: "10px 16px",
            margin: "8px 16px",
            fontSize: 13,
            color: "#60a5fa",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>💬 {directMessage}</span>
          <button
            className="btn-ghost"
            onClick={() => setDirectMessage("")}
            style={{ fontSize: 12, padding: "2px 6px", marginLeft: 12 }}
          >
            ✕
          </button>
        </div>
      )}

      {announcement && (
        <AnnouncementBanner
          message={announcement}
          onDismiss={() => setAnnouncement("")}
        />
      )}

      {isPaused && (
        <div
          style={{
            background: "rgba(251,191,36,0.1)",
            border: "1px solid rgba(251,191,36,0.3)",
            borderRadius: 8,
            padding: "10px 16px",
            margin: "8px 16px",
            fontSize: 13,
            color: "var(--amber)",
            textAlign: "center",
          }}
        >
          ⏸{" "}
          {pauseMessage ||
            "The instructor is on a short break — will be back shortly!"}
        </div>
      )}

      {isLocked && !isInQueue && (
        <div
          style={{
            background: "rgba(248,113,113,0.08)",
            border: "1px solid rgba(248,113,113,0.25)",
            borderRadius: 8,
            padding: "10px 16px",
            margin: "8px 16px",
            fontSize: 13,
            color: "var(--red)",
            textAlign: "center",
          }}
        >
          🚪 The queue is closed to new students for today.
        </div>
      )}

      <main id="main-content" className={styles.content}>
        {searching ? (
          <div className={styles.noSession}>
            <p style={{ color: "var(--txt-dim)", fontSize: 13 }}>
              Looking for an active queue…
            </p>
          </div>
        ) : sessionEnded ? (
          <div className={styles.noSession}>
            <div className={styles.noSessionIcon} aria-hidden="true">
              🚪
            </div>
            <p>This session has ended.</p>
            <p className={styles.hint}>
              See the note above for details. Dismiss it to check for another
              open queue.
            </p>
          </div>
        ) : availableSessions.length > 1 && !session ? (
          <SessionPicker sessions={availableSessions} onPick={pickSession} />
        ) : !session ? (
          <div className={styles.noSession}>
            <div className={styles.noSessionIcon} aria-hidden="true">
              ⬡
            </div>
            <p>No active queue session found.</p>
            <p className={styles.hint}>
              Check back when your instructor opens the queue.
            </p>
            <button
              className="btn-ghost"
              onClick={findActiveSessions}
              style={{ marginTop: 16, fontSize: 13 }}
            >
              ↻ Refresh
            </button>
          </div>
        ) : isCalled ? (
          <div className={`${styles.calledCard} pulse-gold`}>
            <div className={styles.calledIcon} aria-hidden="true">
              📢
            </div>
            <h2>It's your turn!</h2>
            <p>Come into the office once the current student leaves.</p>
            {session.instructor_name && (
              <p
                style={{ fontSize: 12, color: "var(--txt-dim)", marginTop: 8 }}
              >
                {session.instructor_name}'s queue
              </p>
            )}
          </div>
        ) : isInQueue ? (
          <>
            <div className={styles.positionCard}>
              <div className={styles.positionLabel}>
                {isPooling ? "Pooling" : "Your position"}
              </div>
              <div
                className={styles.positionNumber}
                style={
                  isPooling
                    ? { fontSize: "1.4rem", color: "var(--amber)" }
                    : undefined
                }
              >
                {isPooling ? "🎲" : position}
              </div>
              <div className={styles.positionMeta}>
                {isPooling
                  ? `${total} student${total !== 1 ? "s" : ""} pooling — order not set yet`
                  : `${total} student${total !== 1 ? "s" : ""} waiting`}
                {session.instructor_name && (
                  <span
                    style={{
                      display: "block",
                      fontSize: 11,
                      color: "var(--txt-faint)",
                      marginTop: 4,
                    }}
                  >
                    {session.instructor_name}'s queue
                  </span>
                )}
              </div>
              {!isPooling && (
                <WaitPredictionLine prediction={queueStatus?.wait_prediction} />
              )}

              {!isPooling && position === 1 && !isCalled && (
                <div className={styles.nextUpBadge}>🪑 {nextUpMessage}</div>
              )}
            </div>
            <button
              className={`btn-danger ${styles.leaveBtn}`}
              onClick={leaveQueue}
              disabled={loading}
            >
              Leave Queue
            </button>
          </>
        ) : (
          <>
            <QueueStatusLine session={session} />
            <CompetencyPicker
              session={session}
              loading={loading}
              onJoin={joinQueue}
            />
          </>
        )}

        {error && (
          <div className={styles.error}>
            {error}
            <button onClick={() => setError("")}>✕</button>
          </div>
        )}

        {swapRequests.length > 0 && (
          <div className={styles.swapSection}>
            <div className={styles.swapHeader}>Swap Requests</div>
            {swapRequests.map((swap) => (
              <div key={swap.swap_id} className={`${styles.swapCard} card`}>
                <p>A student wants to swap positions with you.</p>
                <div className={styles.swapActions}>
                  <button
                    className="btn-primary"
                    onClick={() => respondToSwap(swap.swap_id, true)}
                  >
                    Accept
                  </button>
                  <button
                    className="btn-ghost"
                    onClick={() => respondToSwap(swap.swap_id, false)}
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {rules && session && (
          <div className={`${styles.rulesCard} card`}>
            <div className={styles.rulesHeader}>Queue Rules</div>
            <div className={styles.rulesList}>
              <div className={styles.rule}>
                <span className={styles.ruleLabel}>Policy</span>
                <span className={styles.ruleValue}>
                  {POLICY_LABELS[rules.policy_label] || rules.policy_label}
                </span>
              </div>
              {rules.class_priority_active && (
                <div className={styles.rule}>
                  <span className={styles.ruleLabel}>Priority</span>
                  <span className={styles.ruleValue}>
                    {rules.class_priority_courses.join(", ")} students go first
                  </span>
                </div>
              )}
              {rules.max_sessions_per_day && (
                <div className={styles.rule}>
                  <span className={styles.ruleLabel}>Daily limit</span>
                  <span className={styles.ruleValue}>
                    {rules.max_sessions_per_day} session
                    {rules.max_sessions_per_day !== 1 ? "s" : ""} per day
                  </span>
                </div>
              )}
            </div>
            {/* The policy name is the moment a student wonders what it means —
                especially under Pooled, where they see a dice instead of a
                position. Link straight to the explanation rather than making
                them find it from the manual's front page. */}
            <a className={styles.rulesLink} href="/manual/policies.html">
              What do these rules mean?
            </a>
          </div>
        )}
      </main>

      {/* Always-visible way into the manual. On phones this is the only one in
          persistent chrome, since the header link is hidden there. */}
      <footer className={styles.pageFooter}>
        <a href="/manual/">Student Manual</a>
        <span aria-hidden="true">·</span>
        <a href="/manual/faq.html">Common Questions</a>
      </footer>

      {nedry && (
        <NedryModal
          message={nedry.message}
          code={nedry.code}
          imageUrl={nedry.imageUrl}
          imageAlt={nedry.imageAlt}
          onClose={() => setNedry(null)}
        />
      )}
    </div>
  );
}
